import { type FieldDef, multiValues } from '@crm/shared';

export function formatMoney(v: unknown, currency: string): string {
  if (typeof v !== 'number') return '';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  } catch {
    return v.toLocaleString();
  }
}

/** The Riyadh day `days` from today (Riyadh is UTC+3 all year), as YYYY-MM-DD: toISOString() alone gives the UTC day. */
export function riyadhDay(days = 0): string {
  return new Date(Date.now() + 3 * 3600_000 + days * 86_400_000).toISOString().slice(0, 10);
}

/** Today's date in Riyadh, YYYY-MM-DD (not UTC: between midnight and 3 a.m. UTC is still yesterday). */
export function riyadhToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' });
}

export function formatDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T00:00:00`) : new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTime(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  return new Date(v).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Plain-text display of a field value; references and stages resolve through `refs`. */
export function displayValue(f: FieldDef, v: unknown, refs: Record<string, string>, currency: string): string {
  if (v === null || v === undefined || v === '') return '';
  switch (f.type) {
    case 'currency':
      return formatMoney(v, currency);
    case 'percent':
      return typeof v === 'number' ? `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}%` : `${v}%`;
    case 'number':
      return typeof v === 'number' ? v.toLocaleString() : String(v);
    case 'date':
      return formatDate(v);
    case 'datetime':
      return formatDateTime(v);
    case 'boolean':
      return v ? 'Yes' : 'No';
    case 'picklist':
      return f.options?.find((o) => o.value === v)?.label ?? String(v);
    case 'multipicklist':
      return multiValues(v).map((x) => f.options?.find((o) => o.value === x)?.label ?? x).join(', ');
    case 'reference':
    case 'user':
    case 'stage':
    case 'lookup':
      return refs[String(v)] ?? 'Unavailable record';
    default:
      return String(v);
  }
}
